import { useRef, useEffect, useCallback, useMemo } from "react";
import { useTheme } from "../../../providers/ThemeProvider";
import { getChartColors } from "../../../theme/chartTokens";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../../lib/utils";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  ChartData,
  Chart,
  TooltipModel,
} from "chart.js";
import { Line } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import DateRangePickerButton from "../../../components/DateRangePicker/DateRangePickerButton";
import { IDateRangePreset } from "../../../interfaces";
import { APP_PLATFORM_LOGO_MAP } from "../../../shared/constants";
import {
  TrendUpIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
} from "@phosphor-icons/react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
);

interface ChartDataPoint {
  date: string;
  totalCost_with_tax: number;
  estimateRevenue: number;
  totalNet_with_tax: number;
  iapRevenue: number;
}

interface AppRevenueChartProps {
  data: ChartDataPoint[];
  onDateRangeChange: (startDate: Date, endDate: Date) => void;
  loading: boolean;
  setSelectedPresetForChart: (preset: IDateRangePreset | undefined) => void;
  selectedPresetForChart?: IDateRangePreset;
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
}

// ── Tooltip helpers ───────────────────────────────────────────────────────────

function fmt(v: number) {
  return formatCurrency(v ?? 0, "USD");
}

function row(label: string, value: number, color?: string, bold = false) {
  return `<div style="display:flex;justify-content:space-between;align-items:center;gap:16px;min-width:190px">
    <span style="font-size:11px;color:${color ?? "#d1d5db"};${bold ? "font-weight:700" : ""}">${label}</span>
    <span style="font-size:11px;font-weight:700;color:${bold ? "#fff" : "#e5e7eb"};white-space:nowrap">${fmt(value)}</span>
  </div>`;
}

function subRow(iconSrc: string, label: string, value: number) {
  return `<div style="display:flex;justify-content:space-between;align-items:center;gap:16px;padding-left:12px;border-left:2px solid #374151">
    <span style="display:flex;align-items:center;gap:5px;font-size:10px;color:#9ca3af">
      <img src="${iconSrc}" style="width:11px;height:11px;object-fit:contain" />
      ${label}
    </span>
    <span style="font-size:10px;font-weight:600;color:#d1d5db;white-space:nowrap">${fmt(value)}</span>
  </div>`;
}

function divider() {
  return `<div style="border-top:1px solid #374151;margin:6px 0"></div>`;
}

// ── Summary stat card ─────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: number;
  sub: string;
  accent: string;         // tailwind color token e.g. "green"
  icon: React.ReactNode;
}

const STAT_DARK_CLASSES: Record<string, string> = {
  green:  "dark:bg-green-900/20 dark:border-green-800/40",
  red:    "dark:bg-red-900/20 dark:border-red-800/40",
  blue:   "dark:bg-blue-900/20 dark:border-blue-800/40",
  indigo: "dark:bg-indigo-900/20 dark:border-indigo-800/40",
};

function StatCard({ label, value, sub, accent, icon }: StatCardProps) {
  const darkClasses = STAT_DARK_CLASSES[accent] ?? "";
  return (
    <div
      className={`flex-1 min-w-0 rounded-2xl border bg-${accent}-50 ${darkClasses} border-${accent}-100 px-5 py-4 flex items-start gap-3`}
    >
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-${accent}-100 text-${accent}-600`}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <p className={`text-xs font-semibold text-${accent}-600 uppercase tracking-wide mb-0.5`}>
          {label}
        </p>
        <p className="text-lg font-bold text-gray-900 dark:text-gray-100 leading-tight truncate">
          {formatCurrency(value, "USD")}
        </p>
        <p className="text-[11px] text-gray-400 dark:text-neutral-500 mt-0.5">{sub}</p>
      </div>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

const AppRevenueChart = ({
  data,
  onDateRangeChange,
  loading,
  setSelectedPresetForChart,
  dateRange,
  selectedPresetForChart,
}: AppRevenueChartProps) => {
  const { t } = useTranslation();
  const chartRef = useRef<Chart<"line"> | null>(null);
  const { isDark } = useTheme();
  const colors = useMemo(() => getChartColors(isDark), [isDark]);

  // Period totals computed from chart data
  const totals = data.reduce(
    (acc, d) => ({
      revenue: acc.revenue + (d.estimateRevenue ?? 0) + (d.iapRevenue ?? 0),
      spend: acc.spend + (d.totalCost_with_tax ?? 0),
      net: acc.net + (d.totalNet_with_tax ?? 0),
    }),
    { revenue: 0, spend: 0, net: 0 }
  );
  const netMargin =
    totals.revenue > 0 ? ((totals.net / totals.revenue) * 100).toFixed(1) : "0";

  const buildTooltip = useCallback(
    (index: number) => {
      const point = data[index];
      if (!point) return "";

      const admob = point.estimateRevenue ?? 0;
      const iap = point.iapRevenue ?? 0;
      const totalRev = admob + iap;
      const cost = point.totalCost_with_tax ?? 0;
      const net = point.totalNet_with_tax ?? 0;

      return `
        ${row("Revenue", totalRev, "#22c55e", true)}
        <div style="display:flex;flex-direction:column;gap:3px;margin-top:3px">
          ${subRow(APP_PLATFORM_LOGO_MAP.google_admob, "AdMob", admob)}
          ${subRow(APP_PLATFORM_LOGO_MAP.apple, "IAP", iap)}
        </div>
        ${divider()}
        ${row("Ad Spend", cost, "#ef4444", true)}
        ${divider()}
        ${row("Net", net, "#818cf8", true)}
      `;
    },
    [data]
  );

  const externalTooltip = useCallback(
    (context: { chart: Chart; tooltip: TooltipModel<"line"> }) => {
      const { chart, tooltip } = context;
      const tooltipEl = document.getElementById("app-revenue-chart-tooltip");
      if (!tooltipEl) return;

      if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = "0";
        tooltipEl.style.transform = "translateY(6px) scale(0.97)";
        return;
      }

      const index = tooltip.dataPoints?.[0]?.dataIndex ?? 0;
      const label = tooltip.dataPoints?.[0]?.label ?? "";

      const dateStr = (() => {
        if (!label) return "";
        const datePart = label.includes("T") ? label.split("T")[0] : label;
        const parts = datePart.split("-");
        if (parts.length === 3) {
          const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
          return `${parts[2]} ${months[parseInt(parts[1]) - 1]} ${parts[0]}`;
        }
        return label;
      })();

      tooltipEl.innerHTML = `
        <div style="font-size:12px;font-weight:700;color:#fff;margin-bottom:8px">${dateStr}</div>
        <div style="display:flex;flex-direction:column;gap:4px">
          ${buildTooltip(index)}
        </div>
      `;

      const canvasRect = chart.canvas.getBoundingClientRect();
      const x = canvasRect.left + tooltip.caretX;
      const y = canvasRect.top + tooltip.caretY;

      const tooltipWidth = tooltipEl.offsetWidth || 220;
      const tooltipHeight = tooltipEl.offsetHeight || 200;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const left = x + tooltipWidth + 16 > viewportWidth ? x - tooltipWidth - 16 : x + 16;
      const top = Math.max(8, Math.min(y - tooltipHeight / 2, viewportHeight - tooltipHeight - 8));

      tooltipEl.style.left = `${left}px`;
      tooltipEl.style.top = `${top}px`;

      requestAnimationFrame(() => {
        tooltipEl.style.opacity = "1";
        tooltipEl.style.transform = "translateY(0) scale(1)";
      });
    },
    [buildTooltip]
  );

  // 3 merged datasets: Revenue (AdMob + IAP), Spend, Net
  const chartData: ChartData<"line"> = {
    labels: data.map((d) => d.date),
    datasets: [
      {
        label: t("dashboard.revenueVsSpend.revenue"),
        data: data.map((d) => (d.estimateRevenue ?? 0) + (d.iapRevenue ?? 0)),
        borderColor: colors.revenue,
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 2,
        pointHoverRadius: 5,
      },
      {
        label: t("apps.columns.totalCost"),
        data: data.map((d) => d.totalCost_with_tax),
        borderColor: colors.spend,
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 2,
        pointHoverRadius: 5,
      },
      {
        label: t("dashboard.summary.totalNet"),
        data: data.map((d) => d.totalNet_with_tax),
        borderColor: colors.profit,
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 2,
        pointHoverRadius: 5,
      },
    ],
  };

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: { size: 12, weight: "bold" },
          color: colors.legendText,
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 12,
          padding: 20,
        },
      },
      tooltip: {
        enabled: false,
        external: externalTooltip,
      },
      zoom: {
        pan: { enabled: true, mode: "x" },
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          mode: "x",
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 18,
          font: { family: "var(--font-inter)", size: 11 },
          color: colors.axisText,
          callback: function (_value, index) {
            const label = this.getLabelForValue(index);
            if (label && typeof label === "string") {
              const datePart = label.includes("T") ? label.split("T")[0] : label;
              const parts = datePart.split("-");
              if (parts.length === 3) {
                const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                return `${parts[2]} ${months[parseInt(parts[1]) - 1]}`;
              }
            }
            return label;
          },
        },
      },
      y: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          font: { family: "var(--font-inter)", size: 11 },
          color: colors.axisText,
          callback: function (tickValue: number | string) {
            if (typeof tickValue === "number") return formatCurrency(tickValue, "USD");
            return tickValue;
          },
        },
      },
    },
  };

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const apply = () => {
      const { ctx, chartArea } = chart;
      if (!chartArea) return;
      const h = chartArea.bottom;
      const gradientDefs: [string, string][] = [
        colors.gradients.revenue,
        colors.gradients.spend,
        colors.gradients.profit,
      ];
      gradientDefs.forEach(([start, end], i) => {
        const g = ctx.createLinearGradient(0, 0, 0, h);
        g.addColorStop(0, start);
        g.addColorStop(1, end);
        chart.data.datasets[i].backgroundColor = g;
      });
      chart.update();
    };

    setTimeout(apply, 100);
  }, [data, isDark, colors]);

  return (
    <div className="flex flex-col gap-5">
      {/* External tooltip — fixed position, outside chart DOM */}
      <div
        id="app-revenue-chart-tooltip"
        style={{
          position: "fixed",
          background: colors.tooltipBg,
          border: `1px solid ${colors.tooltipBorder}`,
          borderRadius: "10px",
          padding: "12px 14px",
          pointerEvents: "none",
          transition: "opacity 0.18s ease, transform 0.18s cubic-bezier(0.34,1.56,0.64,1)",
          opacity: 0,
          transform: "translateY(6px) scale(0.97)",
          zIndex: 9999,
          minWidth: "200px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          willChange: "transform, opacity",
        }}
      />

      {/* Date range picker */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400 font-medium">
          {data.length > 0
            ? `${data.length} data points`
            : "No data for selected range"}
        </p>
        <DateRangePickerButton
          onDateRangeChange={onDateRangeChange}
          onPresetChange={setSelectedPresetForChart}
          initialPreset={selectedPresetForChart?.id}
          initialEndDate={dateRange.endDate}
          initialStartDate={dateRange.startDate}
        />
      </div>

      {loading ? (
        <>
          {/* Skeleton stat cards */}
          <div className="flex gap-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="flex-1 h-20 rounded-2xl bg-gray-100 dark:bg-neutral-800 animate-pulse"
              />
            ))}
          </div>
          {/* Skeleton chart */}
          <div className="h-[calc(100vh-460px)] rounded-2xl bg-gray-100 dark:bg-neutral-800 animate-pulse" />
        </>
      ) : (
        <>
          {/* Summary stat cards */}
          <div className="flex gap-3">
            <StatCard
              label="Revenue"
              value={totals.revenue}
              sub="AdMob + IAP"
              accent="green"
              icon={<TrendUpIcon size={18} weight="duotone" />}
            />
            <StatCard
              label="Ad Spend"
              value={totals.spend}
              sub="Total cost (incl. tax)"
              accent="red"
              icon={<ChartBarIcon size={18} weight="duotone" />}
            />
            <StatCard
              label="Net Profit"
              value={totals.net}
              sub={`${netMargin}% margin`}
              accent="indigo"
              icon={<CurrencyDollarIcon size={18} weight="duotone" />}
            />
          </div>

          {/* Chart */}
          <div className="h-[calc(100vh-460px)] min-h-[280px]">
            <Line key={isDark ? "dark" : "light"} ref={chartRef} data={chartData} options={options} />
          </div>
        </>
      )}
    </div>
  );
};

export default AppRevenueChart;
